import { useState, useEffect, useCallback } from 'react'
import { chatterService, ChatterConversation } from '../services/chatterService'

interface UseChatterListReturn {
  conversations: ChatterConversation[]
  loading: boolean
  error: string | null
  unreadCount: number
  refreshConversations: () => Promise<void>
  refreshUnreadCount: () => Promise<void>
}

export const useChatterList = (): UseChatterListReturn => {
  const [conversations, setConversations] = useState<ChatterConversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unreadCount, setUnreadCount] = useState(0)

  // Load conversations
  const loadConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true)
      }
      setError(null)
      
      const data = await chatterService.getConversations()
      setConversations(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations')
      console.error('Error loading chatter conversations:', err)
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [])

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await chatterService.getUnreadCount()
      setUnreadCount(count)
    } catch (err) {
      console.error('Error loading unread count:', err)
    }
  }, [])

  // Refresh functions
  const refreshConversations = useCallback(async () => {
    await loadConversations()
  }, [loadConversations])

  const refreshUnreadCount = useCallback(async () => {
    await loadUnreadCount()
  }, [loadUnreadCount])

  // Load data on mount
  useEffect(() => {
    loadConversations()
    loadUnreadCount()
  }, [loadConversations, loadUnreadCount])

  // Set up periodic refresh for both unread count and conversations (silent refresh to avoid flicker)
  useEffect(() => {
    const interval = setInterval(() => {
      loadUnreadCount()
      loadConversations(true) // Silent refresh to avoid loading flicker
    }, 10000) // Refresh every 10 seconds for more responsive notifications

    return () => clearInterval(interval)
  }, [loadUnreadCount, loadConversations])

  // Listen for custom events to refresh conversations immediately
  useEffect(() => {
    const handleNewMessage = (event: CustomEvent) => {
      // Only refresh if the message is from someone else (not the ISO user)
      const messageData = event.detail?.messageData
      if (messageData && messageData.senderType === 'iso') {
        // ISO sent the message - just update conversations silently, no notifications
        loadConversations(true)
        return
      }
      
      // Message from external user (client/salesforce) - show notifications
      loadConversations(true)
      loadUnreadCount()
    }

    const handleMessageRead = () => {
      loadConversations(true) // Silent refresh when messages are read
      loadUnreadCount()
    }

    const handleIsoMessage = () => {
      // When ISO sends a message, just update conversations silently without triggering notifications
      loadConversations(true)
    }

    // Listen for custom events from other parts of the app
    window.addEventListener('chatter:new-message', handleNewMessage)
    window.addEventListener('chatter:message-read', handleMessageRead)
    window.addEventListener('chatter:iso-message-sent', handleIsoMessage)

    return () => {
      window.removeEventListener('chatter:new-message', handleNewMessage)
      window.removeEventListener('chatter:message-read', handleMessageRead)
      window.removeEventListener('chatter:iso-message-sent', handleIsoMessage)
    }
  }, [loadConversations, loadUnreadCount])

  return {
    conversations,
    loading,
    error,
    unreadCount,
    refreshConversations,
    refreshUnreadCount
  }
}
